import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export type UserType = 'individual' | 'apartment_owner' | 'apartment_admin' | 'community_member';

export interface UserProfile {
  id: string;
  email: string;
  username?: string;
  user_type: UserType;
  properties: Array<{
    id: string;
    name: string;
    property_type: 'individual' | 'small_apartment' | 'large_community';
    role: 'admin' | 'member';
    max_users: number;
    current_user_count?: number;
    calendar_enabled?: boolean;
    marketplace_enabled?: boolean;
    lawn_management_enabled?: boolean;
    vendor_management_enabled?: boolean;
    workers_management_enabled?: boolean;
    visitor_management_enabled?: boolean;
    facility_booking_enabled?: boolean;
    guard_management_enabled?: boolean;
  }>;
}

export const useUserType = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setUserProfile(null);
        setLoading(false);
        return;
      }

      // First check if user exists in our users table
      let { data: userProfile, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      // If user doesn't exist, create them using metadata from signup
      if (!userProfile) {
        // Check if user_type was provided during signup
        const userTypeFromMetadata = user.user_metadata?.user_type as UserType | undefined;

        const { data: newUser, error: createError } = await supabase
          .from('users')
          .upsert([{
            id: user.id,
            email: user.email!,
            user_type: userTypeFromMetadata || null,
            username: user.user_metadata?.username || null
          }], {
            onConflict: 'id'
          })
          .select()
          .single();

        if (createError) {
          throw createError;
        }
        userProfile = newUser;
      } else if (userError) {
        throw userError;
      }

      // Fetch user's properties and roles
      const { data: propertyUsers, error: propError } = await supabase
        .from('property_users')
        .select(`
          role,
          property:properties (
            id,
            name,
            property_type,
            max_users,
            calendar_enabled,
            marketplace_enabled,
            lawn_management_enabled,
            vendor_management_enabled,
            workers_management_enabled,
            visitor_management_enabled,
            facility_booking_enabled,
            guard_management_enabled
          )
        `)
        .eq('user_id', user.id);

      if (propError) throw propError;

      // Also fetch properties owned directly by the user
      const { data: ownedProperties, error: ownedError } = await supabase
        .from('properties')
        .select('id, name, property_type, max_users, calendar_enabled, marketplace_enabled, lawn_management_enabled, vendor_management_enabled, workers_management_enabled, visitor_management_enabled, facility_booking_enabled, guard_management_enabled')
        .eq('user_id', user.id);

      if (ownedError) throw ownedError;

      // Map owned properties (individual user properties)
      const ownedProps = (ownedProperties || []).map(prop => ({
        ...prop,
        role: 'admin' as const,
        current_user_count: 0
      }));

      // Map property_users memberships (multi-user properties)
      const memberProps = (propertyUsers || [])
        .filter(pu => pu.property && typeof pu.property === 'object')
        .map(pu => ({
          ...pu.property,
          role: pu.role,
          current_user_count: 0
        }));

      // Deduplicate properties by ID (prefer property_users role over owned)
      const propertiesMap = new Map();

      // Add owned properties first
      ownedProps.forEach(prop => {
        propertiesMap.set(prop.id, prop);
      });

      // Override with member properties (has the actual role from property_users)
      memberProps.forEach(prop => {
        propertiesMap.set(prop.id, prop);
      });

      const properties = Array.from(propertiesMap.values());

      setUserProfile({
        ...userProfile,
        properties
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user profile');
    } finally {
      setLoading(false);
    }
  };

  const createUserProfile = async (userType: UserType, username?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { error } = await supabase
        .from('users')
        .insert([{
          id: user.id,
          email: user.email!,
          user_type: userType,
          username: username || null
        }]);

      if (error) throw error;

      // Refresh the user profile
      await fetchUserProfile();
      return true;
    } catch (err) {
      throw err;
    }
  };

  const canEdit = (feature: string): boolean => {
    if (!userProfile) return false;
    return userProfile.properties.some(prop => prop.role === 'admin');
  };

  const canManageUsers = (): boolean => {
    if (!userProfile) return false;
    return userProfile.properties.some(prop =>
      prop.property_type === 'small_apartment' && prop.role === 'admin'
    );
  };

  // True when the user is a community member with no admin role on any property
  const isResidentOnly = (): boolean => {
    if (!userProfile?.properties?.length) return false;
    return userProfile.properties.every(prop => prop.role === 'member');
  };

  return {
    userProfile,
    loading,
    error,
    createUserProfile,
    canEdit,
    canManageUsers,
    isResidentOnly,
    refetch: fetchUserProfile
  };
};